import { useQuery } from '@apollo/client';

import type { Carousel, Recommender } from '@coursera/event-pulse-types';

import CareerRolesQuery from 'bundles/unified-description-page-common/queries/CareerRolesQuery.graphql';
import type {
  CareerRoleCollectionFragment,
  SkillCareerRoleFragment as CareerRoleFragment,
  CareerRolesQueryQuery as CareerRolesQueryType,
} from 'bundles/unified-description-page-common/queries/__generated__/CareerRolesQuery';

export type CareerRolesCollectionType = Omit<CareerRoleCollectionFragment, 'entities'> & {
  entities?: Array<CareerRoleFragment>;
};

type Props = {
  skip: boolean;
  productSlug?: string;
};

const normalizeCareerRolesCollection = (
  content: CareerRoleCollectionFragment | undefined
): CareerRolesCollectionType | undefined => {
  if (!content) return undefined;

  const collection = {
    ...content,
    entities: content?.entities?.elements
      ?.map((ele) => {
        if (!ele || ele.__typename !== 'Skill_CareerRole') return null;
        return ele;
      })
      .filter(Boolean),
  };

  return collection;
};

const useCareerRolesQuery = ({ skip, productSlug }: Props) => {
  const { loading, data } = useQuery<CareerRolesQueryType>(CareerRolesQuery, {
    variables: {
      queryCollectionId: 'roles-for-specialization-slug',
      additionalInput: {
        // eslint-disable-next-line camelcase
        specialization_slug: productSlug,
      },
    },
    skip: skip || !productSlug,
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
    ssr: false,
  });

  return {
    loading,
    data: normalizeCareerRolesCollection(data?.Collection?.queryCollection),
    eventingPathwaysData: loading
      ? undefined
      : {
          recommender: {
            hash: data?.Collection?.queryCollection?.recommenderConfigurationHash,
            context: data?.Collection?.queryCollection?.context,
          } as Recommender,
          carousel: {
            id: data?.Collection?.queryCollection?.id,
          } as Carousel,
        },
  };
};

export default useCareerRolesQuery;
